// GENERAL
import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

// COMPONENTS
import { BaseText } from "../../../../../../components";
import { BundleItem, BundleFee, BundleHeader, NestedBundle } from "../../index";

// UTILS
import { formatCurrency } from "../../../../../../utils";
import getImageSrc from "../../helpers/getImageSrc";
import getPlaceholderImage from "../../helpers/getPlaceholderImage";
// TYPES
import { BundleProps, BundleItemTypes } from "../../types";

// ASSETS
import rightAngleSvg from "../../../../../../assets/rightAngle.svg";

const Bundle: React.FC<BundleProps> = ({ orderBundle, fields, hostName, currencyCharacter }) => {
	const {
		bundleItems,
		_extPriceBundleLease,
		_extPriceBundlePurchase,
		bundle,
		quantity,
	} = orderBundle;
	const { name, number, uuid } = bundle;
	const [expandedItems, setExpandedItems] = useState<string[]>([]);
	const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
	const toggleExpand = (id: string) => {
		setExpandedItems((prev) => {
			const isExpanded = prev.includes(id);
			return isExpanded
				? prev.filter((item) => item !== id)
				: [...prev, id];
		});
	};

	///// CLASS NAMES /////
	const parentContainerClasses = `border-1 rounded-xl transition-all duration-200 ${
		hoveredItemId ? "border-secondary-800" : "border-transparent"
	} `;

	///// TEXT FROM FIELDS /////
	const bundleContentsHeading = fields?.orderItems?.headings[1]?.label;
	const additionalFeesHeading = fields?.orderItems?.headings[2]?.label;
	const quantityText = fields?.orderItems?.bundleDetails?.quantity?.prefix;
	const currencySymbol = currencyCharacter;
	const bundlePrefix = fields?.orderItems?.bundleDetails?.bundleTitle?.prefix;
	const bundleBadgeText =
		fields?.orderItems?.bundleDetails?.bundleBadge.label;
	const leaseBadgeText = fields?.orderItems?.bundleDetails?.leaseBadge?.label;
	const addOnBadgeText = fields?.orderItems?.bundleDetails?.addOnBadge?.label;
	const requiredBadgeText =
		fields?.orderItems?.bundleDetails.requiredBadge?.label;
	const partNumberPrefix =
		fields?.orderItems?.bundleDetails?.partNumber?.prefix;

	const kitIdPrefix = fields.orderItems?.bundleDetails?.kitId?.label;
	const trackingNumberPrefix =
		fields.orderItems?.bundleDetails.trackingNumber?.prefix;
	const leaseSuffix = fields.orderItems?.bundleDetails.leasePrice?.suffix;

	// SETTINGS
	const areFeesVisible = fields.orderItems?.headings[2].isVisible;
	const kitIdVisible = fields.orderItems?.bundleDetails.kitId.isVisible;

	const renderBundlePrimaryItemPrice = (bundleItem: BundleItemTypes) => {
		if (bundleItem?.bundleItem?.defaultPriceType.slug == "each") {
			return {
				price: formatCurrency(
					bundleItem?.bundleItem?._unitPrice as number
				),
				isLeaseItem: false,
			};
		} else if (
			bundleItem?.bundleItem?.defaultPriceType.slug !== "each" &&
			!areFeesVisible &&
			_extPriceBundleLease
		) {
			return {
				price: `${formatCurrency(_extPriceBundleLease)}/mo`,
				isLeaseItem: true,
			};
		} else {
			return {
				price: `${formatCurrency(
					bundleItem?.bundleItem?._leasePrice as number
				)}/mo`,
				isLeaseItem: true,
			};
		}
	};

	const renderBundleItemPrice = (bundleItem: BundleItemTypes) => {
		if (bundleItem?.bundleItem?.defaultPriceType?.slug == "each") {
			return formatCurrency(bundleItem?.bundleItem._unitPrice as number);
		} else if (
			bundleItem?.bundleItem?.defaultPriceType?.slug !== "each" &&
			!areFeesVisible
		) {
			return `${formatCurrency(
				bundleItem?.bundleItem?._extPriceBundleLease as number
			)}/mo`;
		} else {
			return `${formatCurrency(
				bundleItem?.bundleItem?._leasePrice as number
			)}/mo`;
		}
	};

	///// EXTRACTING DATA /////
	const primaryItem = useMemo(() => {
		return orderBundle?.bundleItems?.find(
			(bundleItem: BundleItemTypes) =>
				bundleItem?.bundleItem?.bundleItemGroup?.slug === "primary"
		);
	}, [orderBundle.bundleItems]);

	const primaryItemQuantity = primaryItem?.quantity;
	const primaryPartNumberValue = primaryItem?.bundleItem.item.partNumber;
	const primaryTitleValue = primaryItem?.bundleItem.item.title;
	const primaryImageSrc =
		getImageSrc(
			primaryItem?.bundleItem?.item?.itemImages as BundleItemTypes,
			getPlaceholderImage(hostName)
		) || "";
	const primaryItemGroupSlug = primaryItem?.bundleItem?.bundleItemGroup.slug;
	const primaryIsRequired = primaryItem?.bundleItem.isRequired;
	const primaryPrice = renderBundlePrimaryItemPrice(
		primaryItem as BundleItemTypes
	).price;
	const primaryIsLeaseItem = renderBundlePrimaryItemPrice(
		primaryItem as BundleItemTypes
	).isLeaseItem;

	const secondaryAndTertiaryBundleItems = useMemo(() => {
		return [
			...bundleItems.filter((bundleItem: BundleItemTypes) => {
				return (
					bundleItem?.bundleItem?.item.assetType.name !== "FEE" &&
					bundleItem?.bundleItem?.bundleItemGroup?.slug !== "primary"
				);
			}),
		];
	}, [bundleItems]);

	const fees = useMemo(() => {
		return bundleItems.filter(
			(bundleItem: BundleItemTypes) =>
				bundleItem?.bundleItem?.item.assetType.name === "FEE"
		);
	}, [bundleItems]);

	return (
		<div className={parentContainerClasses}>
			<div
				key={uuid}
				className="relative w-full py-3 hover:cursor-pointer"
				onClick={() => toggleExpand(uuid)}
				onMouseEnter={() => setHoveredItemId(uuid)}
				onMouseLeave={() => setHoveredItemId(null)}
				aria-expanded={expandedItems.includes(uuid)}
			>
				<BundleHeader
					hoveredItemId={hoveredItemId}
					expandedItems={expandedItems}
					uuid={uuid}
					extPriceBundleLease={_extPriceBundleLease}
					extPriceBundlePurchase={_extPriceBundlePurchase}
					quantity={quantity}
					name={name}
					number={number}
					quantityText={quantityText}
					leaseBadgeText={leaseBadgeText}
					leaseSuffix={leaseSuffix}
					currencySymbol={currencySymbol}
					bundlePrefix={bundlePrefix}
					bundleBadgeText={bundleBadgeText}
					kitIdVisible={kitIdVisible}
					kitIdPrefix={kitIdPrefix}
				/>
			</div>
			{expandedItems.includes(uuid) && (
				<AnimatePresence>
					<motion.div
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: "auto" }}
						exit={{ opacity: 0, height: 0 }}
						transition={{ duration: 0.3, ease: "easeInOut" }}
						className="flex flex-col items-start ml-1 mt-1 px-2.5 pb-2.5"
					>
						<BaseText
							color="text-navy-500"
							fontFamily="font-inter-semibold"
							tag="p"
							text={bundleContentsHeading}
							size="text-base"
							additionalClasses="pb-2.5 ml-20"
						/>
						<div className="flex flex-col w-full ">
							{primaryItem && (
								<div className="flex">
									<BundleItem
										key={uuid}
										item={primaryItem}
										imageSrc={primaryImageSrc}
										leaseSuffix={leaseSuffix}
										leaseBadgeText={leaseBadgeText}
										currencySymbol={currencySymbol}
										quantityText={quantityText}
										addOnBadgeText={addOnBadgeText}
										requiredBadgeText={requiredBadgeText}
										partNumberPrefix={partNumberPrefix}
										trackingNumberPrefix={
											trackingNumberPrefix
										}
										quantityValue={primaryItemQuantity}
										partNumberValue={primaryPartNumberValue}
										titleValue={primaryTitleValue}
										bundleItemGroupSlug={
											primaryItemGroupSlug
										}
										isRequired={primaryIsRequired}
										price={primaryPrice}
										isLeaseItem={primaryIsLeaseItem}
									/>
								</div>
							)}

							{secondaryAndTertiaryBundleItems.map(
								(
									bundleItem: BundleItemTypes,
									index: number
								) => {
									return bundleItem?.childBundle &&
										bundleItem?.childBundle !== null ? (
										<div className="flex">
											<NestedBundle
												key={`${bundleItem.uuid}-${index}`}
												orderBundle={bundleItem}
												fields={fields}
												hostName={hostName}
											/>
										</div>
									) : (
										<BundleItem
											key={`${bundleItem.uuid}-${index}`}
											item={bundleItem}
											leaseSuffix={leaseSuffix}
											leaseBadgeText={leaseBadgeText}
											currencySymbol={currencySymbol}
											quantityText={quantityText}
											addOnBadgeText={addOnBadgeText}
											requiredBadgeText={
												requiredBadgeText
											}
											partNumberPrefix={partNumberPrefix}
											trackingNumberPrefix={
												trackingNumberPrefix
											}
											quantityValue={bundleItem.quantity}
											partNumberValue={
												bundleItem.bundleItem.item
													.partNumber
											}
											titleValue={
												bundleItem.bundleItem.item.title
											}
											bundleItemGroupSlug={
												bundleItem.bundleItem
													.bundleItemGroup.slug
											}
											isRequired={
												bundleItem.bundleItem.isRequired
											}
											price={renderBundleItemPrice(
												bundleItem
											)}
											imageSrc={getImageSrc(
												bundleItem.bundleItem.item
													.itemImages,
												getPlaceholderImage(hostName)
											)}
										/>
									);
								}
							)}

							{fees.length > 0 && areFeesVisible && (
								<div className="flex flex-col ">
									<div className="flex justify-start">
										<BaseText
											color="text-navy-500"
											fontFamily="font-inter-semibold"
											tag="h3"
											text={additionalFeesHeading}
											size="text-base"
											additionalClasses="mt-2.5 pl-20"
										/>
									</div>

									{fees.map(
										(
											item: BundleItemTypes,
											index: number
										) => (
											<div className="flex">
												<div className="flex justify-center items-start w-[64px] pl-5 mr-4 max-sm:pl-2 max-sm:mr-2">
													<img
														src={rightAngleSvg}
														alt=""
														className="size-12"
													/>
												</div>
												<BundleFee
													key={`${item.uuid}-${index}`}
													bundleFee={item}
													leaseSuffix={leaseSuffix}
													currencySymbol={
														currencySymbol
													}
													partNumberPrefix={
														partNumberPrefix
													}
												/>
											</div>
										)
									)}
								</div>
							)}
						</div>
					</motion.div>
				</AnimatePresence>
			)}
		</div>
	);
};

export default Bundle;
